import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Spinner } from 'ui';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { File, CheckCircle, Edit2 } from 'react-feather';
import classNames from 'classnames';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import * as actions from '../../actions/journeyBuilder';

const JourneyProgress = ({ completedPercentage }) => {
  return (
    <div className="JourneyProgress">
      <div
        className="JourneyProgress-bar"
        data-testid="JourneyProgress-bar"
        style={{
          width: `${completedPercentage}%`,
        }}
      />
    </div>
  );
};

const ShowMore = ({ children }) => {
  const text = children;
  const [isShowMore, setIsShowMore] = useState(true);
  const toggleShowMore = () => {
    setIsShowMore(!isShowMore);
  };
  return (
    <p className="text" data-testid="JourneyViewerHeader-description">
      <PreserveFormatting>
        {isShowMore
          ? `${text.slice(0, 300)}${text.length > 300 ? '...' : ''}`
          : text}
      </PreserveFormatting>
      <button
        className="u-resetBtn"
        type="button"
        onClick={e => {
          e.preventDefault();
          toggleShowMore();
        }}
      >
        <Text
          className="mt-16 h-td-u u-inline"
          variant={TEXT_VARIANTS.BODY_LINK}
        >
          {text.length > 300 && (
            <>{isShowMore ? t('common.show_more') : t('common.show_less')}</>
          )}
        </Text>
      </button>
    </p>
  );
};

const Header = props => {
  const {
    title,
    description,
    completedSteps,
    currentStep,
    totalSteps,
    contentTypeCount,
    isEditing,
    ui,
    journeyId,
    completed,
  } = useSelector(state => state);

  const dispatch = useDispatch();

  const completedPercentage = (completedSteps / totalSteps) * 100;

  const [loading, setLoading] = useState(false);

  return (
    <div className="JourneyViewerHeader" data-testid="JourneyViewerHeader">
      {isEditing && (
        <div
          className="JourneyViewerHeader-editButton"
          onClick={() => dispatch(actions.showEditDetails())}
          onKeyDown={() => dispatch(actions.showEditDetails())}
        >
          {!ui.previewing && (
            <>
              <Edit2 className="JourneyViewerHeader-editIcon" size={16} />
              {t('journeys.builder.edit')}
            </>
          )}
        </div>
      )}
      <p className="JourneyViewerHeader-name">{t('journeys.title')}</p>
      <h1
        className="JourneyViewerHeader-title"
        data-testid="JourneyViewerHeader-title"
      >
        {title}
      </h1>
      <div className="JourneyViewerHeader-content">
        <div
          className={classNames('JourneyViewerHeader-description', {
            'JourneyViewerHeader-description--editing': isEditing,
          })}
        >
          {description && (
            <>
              <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                <ShowMore>{description}</ShowMore>
              </Text>
            </>
          )}

          {!isEditing && currentStep && (
            <div className="JourneyViewerHeader-progress">
              <JourneyProgress
                completedPercentage={completedPercentage.toString()}
              />
              <p
                className="JourneyViewerHeader-completedPercent"
                data-testid="JourneyViewerHeader-completedPercent"
              >
                {Math.floor(completedPercentage)}%{' '}
                {t('journeys.viewer.complete')}
                {' • '}
                {completedSteps} {t('journeys.of').toLowerCase()} {totalSteps}
              </p>
            </div>
          )}
          {!isEditing && <div className="JourneyViewerHeader-divider" />}
        </div>
        {!isEditing && (
          <div
            className={classNames('JourneyViewerHeader-details', {
              'JourneyViewerHeader-details--emptyDescription': !description,
            })}
          >
            <div
              className="JourneyViewerHeader-detail"
              style={{ display: contentTypeCount.Lessons ? 'flex' : 'none' }}
            >
              <File className="JourneyViewerHeader-icon" size={16} />
              <p>
                {t('journeys.lesson', { count: contentTypeCount.Lessons || 0 })}
              </p>
            </div>
            <div
              className="JourneyViewerHeader-detail"
              style={{ display: contentTypeCount.Paths ? 'flex' : 'none' }}
            >
              <Icon
                className="JourneyViewerHeader-icon fz-bigger"
                name="path"
              />
              <p>
                {t('journeys.path', { count: contentTypeCount.Paths || 0 })}
              </p>
            </div>
            <div
              className="JourneyViewerHeader-detail"
              style={{ display: contentTypeCount.Tasks ? 'flex' : 'none' }}
            >
              <CheckCircle className="JourneyViewerHeader-icon" size={16} />
              <p>
                {t('journeys.task', { count: contentTypeCount.Tasks || 0 })}
              </p>
            </div>
          </div>
        )}
      </div>
      {!isEditing && !currentStep && !completed && (
        <button
          onClick={() => {
            setLoading(true);
            request
              .patchJSON(routes.JOURNEY.START(journeyId))
              .then(window.location.reload());
          }}
          className="JourneyViewerHeader-startButton"
          type="button"
          data-testid="JourneyViewerHeader-startButton"
        >
          {loading ? (
            <div className="JourneyViewerHeader-starting">
              <Spinner />
            </div>
          ) : (
            <div>{t('journeys.start_button')}</div>
          )}
        </button>
      )}
    </div>
  );
};

export default Header;
